import type { Messages } from '@ah/i18n';

/** English translation (ADR-015). Any key missing here falls back to el.ts. */
export const en: Messages = {
  auth: {
    login: {
      title: 'Admin sign in',
      email: 'Email',
      password: 'Password',
      submit: 'Sign in',
      submitting: 'Signing in…',
      missingFields: 'Please enter your email and password.',
      wrongPortal: 'This dashboard is for administrators only.',
      failed: 'Sign in failed. Please try again.',
      provisionNotice: 'Admin accounts are provisioned by an operator (',
      provisionNoticeSuffix: ') — there is no self-serve registration for this dashboard.',
      twoFactorTitle: 'Two-factor verification',
      twoFactorIntro: 'Enter the 6-digit code from your authenticator app, or one of your recovery codes.',
      code: 'Code',
      verify: 'Verify',
      verifying: 'Verifying…',
      verifyFailed: 'Verification failed. Please try again.',
      backToLogin: '← Back to sign in',
    },
  },
  nav: {
    dashboard: 'Dashboard',
    backDashboard: '← Dashboard',
    therapists: 'Therapists',
    users: 'Users',
    reviews: 'Reviews',
    content: 'Content',
    specializations: 'Specializations',
    media: 'Media',
    payments: 'Payments',
    reports: 'Reports',
    settings: 'Settings',
    security: 'Security',
    signOut: 'Sign out',
    language: 'Language',
  },
  common: {
    loading: 'Loading…',
    working: 'Working…',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    actionFailed: 'Action failed. Please try again.',
  },
  dashboard: {
    title: 'Admin Dashboard',
    loadError: 'Could not load the overview.',
    totalUsers: 'Total users',
    newLast7Days: '+{{count}} last 7 days',
    clients: 'Clients',
    therapists: 'Therapists',
    totalBookings: 'Total bookings',
    anonymousVsNamed: 'Anonymous vs. named',
    namedSuffix: '{{count}} named',
    calls: 'Calls',
    avgCallDuration: 'Avg. call duration',
    minutesSuffix: '{{minutes}} min',
    messagesSent: 'Messages sent',
    completedRevenue: 'Completed revenue',
    platformFeeSuffix: '{{amount}} platform fee',
    recentBookings: 'Recent bookings',
    scheduled: 'Scheduled',
    status: 'Status',
    mode: 'Mode',
    noBookings: 'No bookings yet.',
    generatedAt: 'Generated {{when}}',
  },
  therapists: {
    title: 'Therapists',
    tabPending: 'Pending review',
    tabApproved: 'Approved',
    tabSuspended: 'Suspended',
    tabRejected: 'Rejected',
    tabAll: 'All',
    loadError: 'Could not load therapists.',
    actionFailed: 'Action failed. Please try again.',
    photoUploadFailed: 'Photo upload failed.',
    saveFailed: 'Saving the profile failed.',
    colName: 'Name',
    colEmail: 'Email',
    colLicense: 'License',
    colSpecialties: 'Specialties',
    colStatus: 'Status',
    colActions: 'Actions',
    approve: 'Approve',
    reject: 'Reject',
    suspend: 'Suspend',
    editProfile: 'Edit profile',
    closeEdit: 'Close',
    noTherapists: 'No therapists in this category.',
    photoLabel: 'Photo (shown on the landing page)',
    noPhotoYet: 'No photo yet',
    tabEl: 'Greek',
    tabEn: 'English',
    requiredBadge: 'required',
    optionalBadge: 'optional',
    completeBadge: '✓ complete',
    incompleteBadge: 'incomplete',
    headlineLabel: 'Headline (e.g. "Clinical Psychologist, 12 years experience")',
    bioLabel: 'Bio (short, shown on the card)',
    introVideoLabel: 'Intro video (URL — optional, shared across both languages)',
    caseTagsLabel: 'Case types (comma-separated tags — distinct from specialties)',
    caseTagsPlaceholderEl: 'panic attacks, grief, burnout',
    caseTagsPlaceholderEn: 'panic attacks, grief, burnout',
    spokenLanguagesLegend: 'Session languages',
    saveProfile: 'Save profile',
  },
  content: {
    title: 'Content',
    landingTitle: 'Landing page',
    landingHint: 'Anything you change here shows up on the landing page (http://localhost:5173) as soon as you hit Publish.',
    legalTitle: 'Legal documents',
    legalHint: 'Terms of Use (/terms) and Privacy Policy (/privacy) — shown in the footer and on the registration page. Update them any time, no redeploy needed.',
    loadError: 'Could not load content.',
    emptyBody: '"{{key}}": the text cannot be empty.',
    draftSaved: 'Draft saved for "{{key}}". Press Publish to make it live.',
    saveFailed: 'Save failed.',
    published: 'Published "{{key}}".',
    publishFailed: 'Publish failed.',
    imageUploaded: 'Image uploaded — press Save & Publish on "{{key}}" to make it live.',
    uploadFailed: 'Upload failed.',
    stateDefault: 'default text (not edited yet)',
    stateDraftNoTranslation: 'draft with no Greek translation',
    stateUnpublished: 'draft — NOT published',
    statePublished: 'published ✓',
    statePublishedNewerDraft: 'published, with a newer draft',
    saveDraft: 'Save draft',
    saveAndPublish: 'Save & Publish',
  },
  specializations: {
    title: 'Specializations',
    slugPlaceholder: 'slug (e.g. anxiety)',
    nameEnPlaceholder: 'Name (en)',
    nameElPlaceholder: 'Name (el, optional)',
    add: 'Add',
    loadError: 'Could not load specializations.',
    actionFailed: 'Action failed. Please try again.',
    createFailed: 'Could not create specialization.',
    colSlug: 'Slug',
    colName: 'Name',
    colStatus: 'Status',
    colActions: 'Actions',
    active: 'Active',
    inactive: 'Inactive',
    activate: 'Activate',
    deactivate: 'Deactivate',
    noItems: 'No specializations yet.',
  },
  media: {
    title: 'Media',
    uploading: 'Uploading…',
    loadError: 'Could not load media.',
    uploadFailed: 'Upload failed.',
    deleteFailed: 'Delete failed.',
    noItems: 'No media uploaded yet.',
  },
  users: {
    title: 'Users',
    tabAll: 'All',
    tabClients: 'Clients',
    tabTherapists: 'Therapists',
    tabAdmins: 'Admins',
    searchPlaceholder: 'Search email / pseudonym / name',
    loadError: 'Could not load users.',
    suspendReasonPrompt: 'Suspension reason (optional):',
    actionFailed: 'Action failed. Please try again.',
    colName: 'Pseudonym / Name',
    colEmail: 'Email',
    colRole: 'Role',
    colStatus: 'Status',
    colActions: 'Actions',
    deleted: 'Deleted',
    suspended: 'Suspended',
    suspendedWithReason: 'Suspended ({{reason}})',
    active: 'Active',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    noUsers: 'No users match this filter.',
  },
  reviews: {
    title: 'Reviews',
    tabAll: 'All',
    tabVisible: 'Visible',
    tabHidden: 'Hidden',
    loadError: 'Could not load reviews.',
    hideReasonPrompt: 'Reason for hiding (optional):',
    actionFailed: 'Action failed. Please try again.',
    colRating: 'Rating',
    colComment: 'Comment',
    colStatus: 'Status',
    colActions: 'Actions',
    hidden: 'Hidden',
    hiddenWithReason: 'Hidden ({{reason}})',
    visible: 'Visible',
    hide: 'Hide',
    unhide: 'Unhide',
    noReviews: 'No reviews in this category.',
  },
  payments: {
    title: 'Payments',
    loadError: 'Could not load payments.',
    retryFailed: 'Retry failed.',
    exportFailed: 'Export failed.',
    downloadFailed: 'Download failed.',
    weeklyPayouts: 'Weekly payouts',
    weeklyPayoutsHintPrefix: 'Batches are created by the',
    weeklyPayoutsHintSuffix: 'operator script (external cron, every Friday). A FAILED payout stays here until retried — it never re-aggregates bookings.',
    colPeriod: 'Period',
    colNet: 'Net',
    colBookings: 'Bookings',
    colStatus: 'Status',
    colActions: 'Actions',
    retrying: 'Retrying…',
    retry: 'Retry',
    noPayouts: 'No payouts yet.',
    invoiceExport: 'Invoice export',
    invoiceExportHint: "Generates the platform's monthly fee ledger — a ZIP of per-booking invoice PDFs and an Excel summary. Neither file includes client or therapist identity.",
    yearLabel: 'Year',
    monthLabel: 'Month',
    generating: 'Generating…',
    generateExport: 'Generate export',
    colMonth: 'Month',
    colInvoices: 'Invoices',
    colFiles: 'Files',
    downloading: 'Downloading…',
    noExports: 'No exports generated yet.',
  },
  paymentsSettings: {
    title: 'Payments settings',
    intro: 'Configure Stripe from here instead of the',
    introMid: "file on the server. A value saved here overrides the matching",
    introSuffix: 'variable; "Revert to .env" clears the override. Secret keys are encrypted at rest and are never shown in full again — only a masked hint.',
    loadError: 'Could not load Stripe settings.',
    saveFailed: 'Save failed.',
    revertFailed: 'Revert failed.',
    fromDashboard: 'from dashboard',
    fromEnv: 'from .env',
    notSet: 'not set',
    connectionStatus: 'Connection status',
    secretKey: 'Secret key',
    notConfigured: 'Not configured',
    liveMode: 'LIVE mode',
    testMode: 'TEST mode',
    platformWebhook: 'Platform webhook',
    connectWebhook: 'Connect webhook',
    lastReceived: 'Last received: {{when}}',
    never: 'never',
    lastChanged: 'Last changed {{when}} by user {{userId}}',
    secretsTitle: 'Secrets',
    stripeSecretKey: 'Stripe secret key',
    platformWebhookSecret: 'Platform webhook secret',
    connectWebhookSecret: 'Connect webhook secret',
    revertToEnv: 'Revert to .env',
    marketplaceTitle: 'Marketplace',
    platformFee: 'Platform fee (%)',
    currency: 'Currency',
    statementDescriptor: 'Statement descriptor',
    statementDescriptorPlaceholder: "Shown on the client's card statement",
    connectUrlsTitle: 'Connect onboarding URLs',
    returnUrl: 'Return URL',
    refreshUrl: 'Refresh URL',
    payoutScheduleTitle: 'Weekly payout schedule',
    payoutScheduleHint: 'Advisory only — there is no in-app job scheduler. This just tells you what to point your external cron at',
    day: 'Day',
    hour: 'Hour (UTC)',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    weekdaySunday: 'Sunday',
    weekdayMonday: 'Monday',
    weekdayTuesday: 'Tuesday',
    weekdayWednesday: 'Wednesday',
    weekdayThursday: 'Thursday',
    weekdayFriday: 'Friday',
    weekdaySaturday: 'Saturday',
  },
  security: {
    title: 'Security',
    loadError: 'Could not load 2FA status.',
    startSetupFailed: 'Could not start setup.',
    incorrectCode: 'Incorrect code — try again.',
    disableFailed: 'Could not disable 2FA — check your password and code.',
    twoFactorTitle: 'Two-factor authentication',
    enabled: 'Enabled',
    enabledSince: ' — since {{when}}',
    enabledHint: 'Every sign-in on this dashboard asks for a code from your authenticator app after your password.',
    disable: 'Disable two-factor authentication',
    accountPassword: 'Account password',
    currentCodeLabel: 'Current authenticator code or a recovery code',
    disabling: 'Disabling…',
    confirmDisable: 'Confirm disable',
    cancel: 'Cancel',
    notEnabled: 'Not enabled.',
    notEnabledHint: 'Adds a mandatory second step to admin sign-in using an authenticator app (Google Authenticator, Authy, 1Password, ...). Strongly recommended for every admin account.',
    starting: 'Starting…',
    setUp: 'Set up two-factor authentication',
    step1Title: '1. Scan the QR code',
    step1Hint: "Open your authenticator app and scan this code. Can't scan? Enter the secret below manually instead.",
    qrAlt: 'Two-factor authentication QR code',
    manualEntrySecret: 'Manual entry secret:',
    step2Title: '2. Enter the current code to confirm',
    confirming: 'Confirming…',
    confirmAndEnable: 'Confirm and enable',
    enabledNowTitle: 'Two-factor authentication is now enabled',
    saveRecoveryCodes: 'Save these 10 recovery codes now — they are shown only this once.',
    recoveryCodesHint: 'If you lose access to your authenticator app, each code lets you sign in one time. Store them somewhere safe (a password manager, printed and locked away) — not in email or a chat message.',
    savedAcknowledgement: 'I have saved these recovery codes somewhere safe',
    done: 'Done',
  },
  reports: {
    title: 'Financial reports',
    intro: 'Captured payments only — authorized-but-not-captured, cancelled, failed, and refunded bookings are never counted as revenue. No client identity appears anywhere on this page or its exports.',
    loadError: 'Could not load the report.',
    sessionsLoadError: 'Could not load sessions.',
    exportFailed: 'Export failed.',
    from: 'From',
    to: 'To',
    groupBy: 'Group by',
    month: 'Month',
    year: 'Year',
    totalTurnover: 'Total turnover ({{count}} sessions)',
    platformRevenue: 'Platform revenue',
    therapistEarnings: 'Therapist earnings',
    byPeriod: 'By {{granularity}}',
    exporting: 'Exporting…',
    exportCsv: 'Export CSV',
    colPeriod: 'Period',
    colTurnover: 'Turnover',
    colPlatformFee: 'Platform fee',
    colTherapistEarnings: 'Therapist earnings',
    colSessions: 'Sessions',
    noPaymentsInPeriod: 'No captured payments in this period.',
    byTherapist: 'By therapist',
    colTherapist: 'Therapist',
    colEarnings: 'Earnings',
    sessionsAction: 'Sessions',
    sessionsFor: 'Sessions — {{name}}',
    colBooking: 'Booking',
    colCaptured: 'Captured',
    colStatus: 'Status',
    colGross: 'Gross',
    colNet: 'Net',
    noSessionsInPeriod: 'No captured sessions in this period.',
  },
};
